/**
 * How the two halves of the IMAP view tell each other that the mailbox changed.
 *
 * The rail's folder tree and the open folder's message list do not share a
 * parent close enough to pass props through - one sits in the folder rail, the
 * other in the section's middle pane. Each still has to hear what the other
 * did: the rail corrects its counts when the list files a message, and the list
 * reads its folder again when the rail marks the whole folder read.
 *
 * A module-level channel rather than a context, because it carries events, not
 * state: nothing here is ever read back, and a provider would re-render both
 * halves on every message for no reason.
 */
import { useEffect, useRef } from "react";
import type { FolderCountChange } from "./folderCounts";

/**
 * Each event names the mailbox it happened in: the rail draws one folder tree
 * per mailbox, and "INBOX.Sent lost an unread message" is about one of them.
 */
export type MailboxEvent =
  | { kind: "counts"; mailbox: string; changes: FolderCountChange[] }
  | { kind: "folderRead"; mailbox: string; path: string };

const listeners = new Set<(event: MailboxEvent) => void>();

export function announceMailbox(event: MailboxEvent): void {
  if (event.kind === "counts" && !event.changes.length) return;
  for (const listener of listeners) listener(event);
}

/** Listen for as long as the calling component is mounted. The latest listener
 *  always runs, so it may close over fresh props without re-subscribing. */
export function useMailboxEvents(listener: (event: MailboxEvent) => void): void {
  const latest = useRef(listener);
  latest.current = listener;
  useEffect(() => {
    const forward = (event: MailboxEvent) => latest.current(event);
    listeners.add(forward);
    return () => {
      listeners.delete(forward);
    };
  }, []);
}
